import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, User } from "lucide-react";

const METRIC_LABELS = {
  exit_velocity: "Exit Velocity",
  average_exit_velocity: "Avg Exit Velocity",
  bat_speed: "Bat Speed",
  ten_yard_sprint: "10-Yard Sprint",
  thirty_yard_sprint: "30-Yard Sprint",
  sixty_yard_sprint: "60-Yard Sprint",
  five_ten_five_shuttle: "5-10-5 Shuttle",
  infield_velocity: "Infield Velocity",
  outfield_velocity: "Outfield Velocity",
  pop_time: "Pop Time",
  catcher_velocity: "Catcher Velocity",
  fastball_velocity: "Fastball Velocity",
  spin_rate: "Spin Rate",
  strike_percentage: "Strike %",
  pitch_movement: "Pitch Movement",
};

const VERIFICATION_BADGE = {
  cbr_verified: {
    label: "CBR Verified",
    classes: "bg-[#E63946] text-white",
    icon: ShieldCheck,
  },
  staff_entered: {
    label: "Staff Entered",
    classes: "bg-[#0A192F] text-white",
    icon: ShieldCheck,
  },
  self_reported: {
    label: "Self Reported",
    classes: "bg-slate-200 text-slate-700",
    icon: User,
  },
};

export default function PlayerMetricsSection({ player }) {
  const [history, setHistory] = useState([]);
  const summary = player?.verified_metrics || {};
  const hasSummary = Object.keys(summary).length > 0;

  useEffect(() => {
    if (!player?.id) return;
    api
      .get(`/players/${player.id}/metrics`)
      .then(({ data }) => setHistory(data.items || []))
      .catch(() => setHistory([]));
  }, [player?.id]);

  if (!hasSummary) {
    return (
      <div className="bg-[#0A192F] text-white p-6 rounded-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline text-[#E63946]">Performance Metrics</div>
            <h3 className="font-display text-xl uppercase tracking-tight mt-1">No verified data yet</h3>
          </div>
          <ShieldAlert className="w-6 h-6 text-white/40" />
        </div>
        <p className="text-sm text-white/70 mt-3">
          This player has not attended a CBR verified testing event yet. Metrics populate automatically when a CBR
          staff member submits exit velocity, 60-yard, pop time, bullpen velocity or other measurements at a camp.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline text-[#E63946]">Performance</div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">Verified Metrics</h3>
        </div>
        <div className="text-[10px] text-slate-500">Best result per metric · CBR Verified preferred</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {Object.entries(summary).map(([metricKey, m]) => {
          const badge = VERIFICATION_BADGE[m.verification_status] || VERIFICATION_BADGE.self_reported;
          const Icon = badge.icon;
          return (
            <div
              key={metricKey}
              data-testid={`metric-${metricKey}`}
              className="border border-slate-200 p-4 bg-white rounded-sm"
            >
              <div className="flex items-center justify-between">
                <div className="label-overline">{METRIC_LABELS[metricKey] || metricKey}</div>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm ${badge.classes}`}>
                  <Icon className="w-3 h-3" /> {badge.label}
                </span>
              </div>
              <div className="font-data text-3xl text-[#0A192F] mt-2">
                {m.value}
                {m.units && <span className="text-base ml-1 text-slate-500">{m.units}</span>}
              </div>
              {m.event_name && (
                <div className="text-[11px] text-slate-500 mt-2 border-t border-slate-100 pt-2">
                  {m.event_name}
                  {m.event_date ? ` · ${new Date(m.event_date).toLocaleDateString("en-CA", { dateStyle: "medium" })}` : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {history.length > 1 && (
        <details className="mt-6 border border-slate-200 rounded-sm">
          <summary className="cursor-pointer p-4 label-overline">View Full Metric History ({history.length})</summary>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-y border-slate-200">
                  <th className="text-left p-3 label-overline">Metric</th>
                  <th className="text-left p-3 label-overline">Value</th>
                  <th className="text-left p-3 label-overline">Event</th>
                  <th className="text-left p-3 label-overline">Date</th>
                  <th className="text-left p-3 label-overline">Source</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const badge = VERIFICATION_BADGE[h.verification_status] || VERIFICATION_BADGE.self_reported;
                  return (
                    <tr key={h.id} className="border-b border-slate-100 last:border-0">
                      <td className="p-3">{METRIC_LABELS[h.metric_type] || h.metric_type}</td>
                      <td className="p-3 font-data">{h.value} {h.units || ""}</td>
                      <td className="p-3">{h.event_name || "—"}</td>
                      <td className="p-3 text-xs">
                        {h.event_date ? new Date(h.event_date).toLocaleDateString("en-CA") : "—"}
                      </td>
                      <td className="p-3">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${badge.classes}`}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
